/**
 * Zuhause — die drei Leisten, ohne DOM.
 * Heute / Gesagt / Tun aus vorhandenen Daten.
 */

export interface ZuhauseZeile {
  id: string;
  titel: string;
  neben?: string;
  ziel: "alltag" | "chat" | "tickets" | "spiegel";
  art?: "meta" | "teaser" | "kurz";
  /** ticket | notification | thread — für Erledigt am Faden. */
  kind?: string | null;
  createdAt?: string | null;
}

export interface ZuhauseLeisten {
  heute: ZuhauseZeile[];
  gesagt: ZuhauseZeile[];
  tun: ZuhauseZeile[];
}

/** Zuhause-Uhr: Wiener Wandzeit, nie Box-, Server- oder Browser-TZ. */
export const ZUHAUSE_ZEITZONE = "Europe/Vienna";

/** Letzter Sonntag eines UTC-Monats (monthIndex 0=Jan). */
function letzterSonntagUtc(jahr: number, monthIndex: number): number {
  const ende = new Date(Date.UTC(jahr, monthIndex + 1, 0));
  return ende.getUTCDate() - ende.getUTCDay();
}

/**
 * EU-DST fuer Europe/Vienna: letzter Sonntag Maerz 01:00 UTC -> CEST (UTC+2),
 * letzter Sonntag Oktober 01:00 UTC -> CET (UTC+1).
 * Kein Intl — 2220/2221 haben toLocaleString/hourCycle im Boot gehaengt.
 */
function wienOffsetStunden(jetzt: Date): number {
  const jahr = jetzt.getUTCFullYear();
  const start = Date.UTC(jahr, 2, letzterSonntagUtc(jahr, 2), 1, 0, 0);
  const ende = Date.UTC(jahr, 9, letzterSonntagUtc(jahr, 9), 1, 0, 0);
  const t = jetzt.getTime();
  return t >= start && t < ende ? 2 : 1;
}

/** Wiener Wandzeit ohne Intl. DateTimeFormat darf den Start nicht blockieren. */
function wienTeile(
  jetzt: Date,
): { year: number; month: number; day: number; hour: number; minute: number } {
  const local = new Date(jetzt.getTime() + wienOffsetStunden(jetzt) * 3_600_000);
  return {
    year: local.getUTCFullYear(),
    month: local.getUTCMonth() + 1,
    day: local.getUTCDate(),
    hour: local.getUTCHours(),
    minute: local.getUTCMinutes(),
  };
}

const WOCHENTAGE = ["So.", "Mo.", "Di.", "Mi.", "Do.", "Fr.", "Sa."];
const MONATE = ["Jän.", "Feb.", "März", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sept.", "Okt.", "Nov.", "Dez."];
/** „So., 6. Sept." — Wiener Zeit, für die Zeile im Zuhause (06.09., Martin). */
export function formatDatum(jetzt: Date = new Date()): string {
  try {
    const { year, month, day } = wienTeile(jetzt);
    const wt = WOCHENTAGE[new Date(Date.UTC(year, month - 1, day)).getUTCDay()] ?? "";
    return `${wt} ${day}. ${MONATE[month - 1] ?? ""}`.trim();
  } catch {
    return "";
  }
}
export function formatUhr(jetzt: Date = new Date(), _zeitzone = ZUHAUSE_ZEITZONE): string {
  try {
    const { hour, minute } = wienTeile(jetzt);
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  } catch {
    return "--:--";
  }
}

/** Stunde 0-23 in Wiener Zeit. */
export function stundeInWien(jetzt: Date = new Date(), _zeitzone = ZUHAUSE_ZEITZONE): number {
  try {
    return wienTeile(jetzt).hour;
  } catch {
    return 0;
  }
}

/** YYYY-MM-DD in Wien — ohne toLocaleDateString/timeZone. */
export function wienTagSchluessel(jetzt: Date = new Date()): string {
  try {
    const { year, month, day } = wienTeile(jetzt);
    return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  } catch {
    return "1970-01-01";
  }
}

export function gruss(jetzt = new Date()): string {
  try {
    const h = stundeInWien(jetzt);
    if (h < 11) return "Guten Morgen";
    if (h < 18) return "Guten Tag";
    return "Guten Abend";
  } catch {
    return "Hallo";
  }
}

/** Erster Vorname — der Gruß bleibt eine Zeile. */
export function vorname(name: string): string {
  const t = name.trim();
  if (!t) return "";
  return t.split(/\s+/)[0] ?? t;
}

/**
 * Wie lange etwas schon wartet — als Tatsache, nicht als Vorwurf.
 *
 * Martin: *„Je länger es dauert, kann eine andere Information dazukommen,
 * sodass ich wirklich versuche, die Frage zu beantworten — weil nur durch
 * Disziplin und Dranbleiben kommt man weiter."*
 *
 * ⚠️ Am ersten Tag steht NICHTS. „seit 0 Tagen" wäre Lärm, und etwas, das
 *    heute entstanden ist, wartet noch nicht — es liegt bereit.
 *
 * Gedacht für jeden wartenden Zustand, nicht nur die Tagesfrage: Spiegel,
 * Fäden, was noch kommt.
 */
export function wartetSeit(tage?: number): string | undefined {
  if (typeof tage !== "number" || !Number.isFinite(tage) || tage < 1) {
    return undefined;
  }
  if (tage === 1) return "seit gestern";
  if (tage < 7) return `seit ${tage} Tagen`;
  const wochen = Math.floor(tage / 7);
  return wochen === 1 ? "seit einer Woche" : `seit ${wochen} Wochen`;
}

export function baueZuhauseLeisten(eingabe: {
  uhr?: string | null;
  wetter?: string | null;
  heuteTitel?: string | null;
  heuteNeben?: string | null;
  gesagt: Array<{ id: string; text: string }>;
  tun: Array<{
    id: string;
    titel: string;
    kind?: string | null;
    createdAt?: string | null;
  }>;
  /**
   * Was aussteht, ohne ein Ticket zu sein — heute: ungelesene Spiegel.
   *
   * 2026-08-29 (Martin: „weil dort ja im Prinzip alles stehen soll, was noch
   * nicht gemacht wurde"): Unter „Offen" standen bis dahin nur Tickets.
   */
  offen?: Array<{
    id: string;
    titel: string;
    schluessel: string;
    art?: string;
    /** Wie viele Tage steht das schon aus (2026-09-05). */
    alterTage?: number;
  }>;
}): ZuhauseLeisten {
  const heute: ZuhauseZeile[] = [];
  const uhr = (eingabe.uhr || "").trim();
  const wetter = (eingabe.wetter || "").trim();
  const teile = [uhr, wetter].filter((x) => x.length > 0);
  if (teile.length) {
    heute.push({
      id: "meta",
      titel: teile.join("  ·  "),
      art: "meta",
      ziel: "chat",
    });
  }
  const titel = (eingabe.heuteTitel || "").trim();
  if (titel) {
    heute.push({
      id: "heute",
      titel: kuerzeTagesbild(titel),
      neben: eingabe.heuteNeben?.trim() || undefined,
      ziel: "alltag",
      art: "teaser",
    });
  }
  const gesagt = eingabe.gesagt
    .filter((g) => istZuhauseGesagt(g.text))
    .map((g) => ({
      id: g.id,
      titel: kuerze(g.text, 160),
      ziel: "chat" as const,
      art: "kurz" as const,
    }))
    .slice(0, 2);
  /*
   * Das Ausstehende steht VOR den Tickets. Begruendung: Ein ungelesener
   * Spiegel ist etwas, das nur Martin selbst tun kann und das mit jedem Tag
   * weniger wert wird — eine E-Mail wartet geduldiger. Auf dem Telefon sind
   * ohnehin nur zwei Karten sichtbar; was vorn steht, entscheidet.
   */
  const offenAlle = (eingabe.offen ?? []).filter((o) => o.titel?.trim());
  const istFrage = (o: { id: string; art?: string }) =>
    o.art === "frage" || o.id.startsWith("frage:");
  const spiegelOffen = offenAlle.filter((o) => !istFrage(o));
  const fragenOffen = offenAlle.filter(istFrage);
  /*
   * ⚠️ NUR EINE Zeile fuer Spiegel, auch wenn sechs offen sind.
   *
   * Der erste Lauf gegen echte Daten zeigte den Fehler sofort: sechs
   * ausstehende Spiegel besetzten BEIDE sichtbaren Offen-Karten des Telefons,
   * und Martins E-Mails verschwanden hinter „und 15 weitere". Aus einem
   * Hinweis war Noergeln geworden — und die Liste, die zeigen soll, was ansteht,
   * zeigte nur noch eine Sorte.
   *
   * Eine Zeile sagt dasselbe: es steht etwas aus. Die Zahl daneben sagt, wie
   * viel. Ist sie abgehakt, rueckt die naechste nach — ein Tag pro Tag, was
   * fuer ein Tagebuch die richtige Geschwindigkeit ist.
   *
   * 2026-09-01: Die Tagesfrage gehoert NICHT in diesen einen Spiegel-Platz.
   * /api/offen legt sie nach vorn; wuuerde sie mitgezaehlt, verschwindet
   * „Spiegel von gestern" — genau das, was Martin auf Zuhause erwartet.
   */
  const ausstehendSpiegel: ZuhauseZeile[] = spiegelOffen.slice(0, 1).map((o) => ({
    id: o.id,
    titel: kuerze(o.titel, 80),
    ziel: "spiegel" as const,
    kind: "spiegel",
    createdAt: null,
    neben:
      spiegelOffen.length > 1
        ? `und ${spiegelOffen.length - 1} weitere`
        : undefined,
  }));
  /*
   * 2026-09-05 (Martin): *„Je länger es dauert, kann eine andere Information
   * dazukommen, sodass ich wirklich versuche, die Frage zu beantworten — weil
   * nur durch Disziplin und Dranbleiben kommt man weiter."*
   *
   * Das Alter steht im vorhandenen `neben`-Feld — dasselbe, das beim Spiegel
   * „und 3 weitere" trägt. Kein zweites Element, keine eigene Klasse.
   *
   * ⚠️ Es TRÄGT, es mahnt nicht. „seit 4 Tagen" ist eine Tatsache; „du hast
   *    schon wieder nicht" wäre ein Vorwurf — und Vorwürfe lässt man
   *    wegsehen, statt hinzusehen.
   */
  const ausstehendFragen: ZuhauseZeile[] = fragenOffen.slice(0, 1).map((o) => ({
    id: o.id,
    titel: kuerze(o.titel, 80),
    ziel: "chat" as const,
    kind: "frage",
    createdAt: null,
    neben: wartetSeit(o.alterTage),
  }));
  const ausstehend = [...ausstehendSpiegel, ...ausstehendFragen];
  const frageSchonDa = ausstehendFragen.length > 0;

  const tun = eingabe.tun
    .map((t) => ({
      id: String(t.id),
      titel: kuerze(t.titel, 80),
      ziel: "tickets" as const,
      kind: t.kind ?? null,
      createdAt: t.createdAt ?? null,
      neben: zuhauseOffenDatum(t.createdAt) || undefined,
    }))
    .filter((t) => t.titel.length > 0)
    .filter((t) => !(frageSchonDa && /tiefe frage des tages/i.test(t.titel)));
  return { heute, gesagt, tun: [...ausstehend, ...tun] };
}

/** Telefon-Zuhause: zwei Offen-Karten. Der Rest bleibt unter Aufgaben. */
export const ZUHAUSE_TELEFON_OFFEN = 2;

/** Zwei Karten auf dem Telefon; Desktop bekommt die volle Liste. Quelle bleibt ganz. */
export function zuhauseTelefonTun(
  zeilen: ZuhauseZeile[],
  telefon: boolean,
): { sichtbar: ZuhauseZeile[]; weitere: number } {
  if (!telefon || zeilen.length <= ZUHAUSE_TELEFON_OFFEN) {
    return { sichtbar: zeilen, weitere: 0 };
  }
  return {
    sichtbar: zeilen.slice(0, ZUHAUSE_TELEFON_OFFEN),
    weitere: zeilen.length - ZUHAUSE_TELEFON_OFFEN,
  };
}

/** „und 4 weitere“ — Zahl ist total − 2, kein Lügen. */
export function zuhauseWeitereSatz(n: number): string {
  if (n <= 0) return "";
  return n === 1 ? "und 1 weitere" : `und ${n} weitere`;
}

/** Erster Blick: eine Zeile, Rest hinter Mehr. */
export function zuhauseBlickOffen(zeilen: ZuhauseZeile[]): {
  zahl: number;
  erste: ZuhauseZeile | null;
  rest: ZuhauseZeile[];
} {
  if (zeilen.length === 0) return { zahl: 0, erste: null, rest: [] };
  return {
    zahl: zeilen.length,
    erste: zeilen[0]!,
    rest: zeilen.slice(1),
  };
}

/** Eine Zeile, kein Kartenstapel. */
export function zuhauseBlickOffenSatz(zahl: number, ersteTitel: string): string {
  const titel = ersteTitel.trim();
  if (zahl <= 0 || !titel) return "";
  if (zahl === 1) return titel;
  return `${zahl} offen · ${titel}`;
}

export const ZUHAUSE_WEGE: ReadonlyArray<{ id: string; label: string; frage: string }> = [
  { id: "wetter", label: "Wetter", frage: "Wie wird das Wetter morgen?" },
  { id: "zug", label: "Zug", frage: "Zugverbindung nach Wien um 16 Uhr." },
  /*
   * 08.09.2026 — Martins Wort ist „Aufgabe". ⚠️ Die FRAGE bleibt, wie sie
   * ist: sie ist der Wortlaut, den das Werkzeug erkennt (reminder_create),
   * und die Bestandszeilen tragen denselben Vorspann.
   */
  { id: "erinnerung", label: "Aufgabe", frage: "Erinnerung in 20 Minuten: Raus mit dir." },
  { id: "gestern", label: "Gestern", frage: "Was habe ich gestern gesagt?" },
  { id: "post", label: "Post", frage: "Welche Mails brauchen eine Antwort?" },
];

/** Nur menschliche Offene — Fäden und Meldungen, keine Entwickler-Tickets. */
export function istZuhauseTun(eintrag: {
  kind?: string | null;
  title?: string;
  titel?: string;
  description?: string | null;
}): boolean {
  const kind = (eintrag.kind || "ticket").toLowerCase();
  if (kind !== "notification" && kind !== "thread") return false;
  const titel = (eintrag.title || eintrag.titel || "").trim();
  if (!titel) return false;
  // Schlaf-Bitte ist ein Abend-Hinweis aus WHOOP, kein Morgen-To-do.
  if (/körper bittet um schlaf/i.test(titel)) return false;
  if (istEntwicklerNotiz(titel, eintrag.description)) return false;
  if (istMeldung(titel)) return false;
  return true;
}

/**
 * Eine Meldung ist keine Aufgabe. (08.09.2026)
 *
 * Martin, zweimal am selben Vormittag: *„Was ist das für eine Meldung, die
 * in Zuhause steht? Was mache ich mit dieser?"* — und, als die Antwort
 * „nichts, hak sie ab" lautete: *„Nochmal, was soll ich damit machen?
 * Warum steht das unter ‚offen'?"*
 *
 * Er hat recht, und die Antwort war meine erste Fassung schuldig geblieben:
 * Unter „Offen" steht, was er TUN soll. „Anthropic-Alert: Opus 5.1 & Haiku
 * 5" kann man nur lesen.
 *
 * ⚠️ Die Unterscheidung lag längst in den Daten und wurde nur nicht
 *    benutzt: Der Auftrags-Läufer schreibt „Erinnerung: …", wenn etwas zu
 *    tun ist (`isReminder`), und „Geplant: …", wenn es ein Bericht ist
 *    (server/scheduled-jobs-runner.ts). Derselbe Läufer schließt Berichte
 *    sogar ausdrücklich aus der Glocken-Zahl aus — die Offen-Liste hat
 *    diese Entscheidung nur nie mitgelesen.
 */
export function istMeldung(titel: string): boolean {
  return /^\s*Geplant:\s/i.test(titel);
}

/**
 * Ein Faden ueber Ticketnummern ist kein Faden. (07.09.2026)
 *
 * Martin: *„Was ist das für eine offene Erinnerung? Das hat nichts mit
 * meinen Aufgaben zu tun, oder?"* — und vorher, ueber dieselbe Zeile:
 * *„Und dann steht auf ‚offene Umsetzung': Tickets 2142, 2214. Warum steht
 * das bitte in meinem Zuhause?"*
 *
 * Gemeint war Ticket 2145, angelegt in der Nacht mit Maja:
 * „Umsetzung Tickets 2142/2143 durch Cloud Code offen — Warten auf
 * Umsetzung von Ticket 2142 (Alterungsfeld/Decay) und 2143."
 *
 * Es trug `kind: "thread"` und kam damit durch jeden Filter: ein Faden ist
 * per Vertrag SEIN Thema. Nur ist das hier kein Thema zum Besprechen,
 * sondern eine Notiz von Vespera an sich selbst.
 *
 * ⚠️ Bewusst ENG: Nur wenn Ticketnummern oder Entwickler-Vokabular
 *    ausdruecklich vorkommen. Ein Faden ueber sein Leben, in dem zufaellig
 *    eine Zahl steht, muss durchkommen — deshalb reicht die Zahl allein
 *    nicht, es braucht das Wort „Ticket" davor.
 */
export function istEntwicklerNotiz(
  titel: string,
  beschreibung?: string | null,
): boolean {
  const text = `${titel} ${beschreibung ?? ""}`;
  return (
    /\bTickets?\s*#?\d{3,}/i.test(text) ||
    /\bCloud\s*Code\b/i.test(text) ||
    /\bpre\.\d{3,}\b/i.test(text)
  );
}

/** Kurzes Datum für eine Offen-Zeile auf dem Telefon (21.08.). */
export function zuhauseOffenDatum(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "";
  const tag = String(d.getDate()).padStart(2, "0");
  const mon = String(d.getMonth() + 1).padStart(2, "0");
  return `${tag}.${mon}.`;
}

/**
 * Wie das Tagesblatt auf Zuhause heißen soll.
 *
 * Martin, 24.08.: das „Guten Morgen" zeigte den Rückblick auf gestern — aber
 * die Überschrift sagte „Heute". Ursache: `alltag.days[0]` ist der jüngste Tag
 * MIT Aufnahmen, nicht der heutige. Ohne Aufnahme heute rutscht dort gestern
 * hinein und trägt trotzdem den falschen Namen.
 *
 * Keine Funktion geht dabei verloren — der Rückblick bleibt stehen, er sagt
 * jetzt nur selbst, von wann er ist.
 *
 * @param blattKey Tagesschlüssel des angezeigten Blatts (YYYY-MM-DD)
 * @param heuteKey Heutiger Tagesschlüssel (wienTagSchluessel())
 */
export function tagesblattTitel(
  blattKey: string | null | undefined,
  heuteKey: string,
): string {
  const b = (blattKey || "").trim();
  if (!b || !/^\d{4}-\d{2}-\d{2}$/.test(b)) return "Heute";
  if (b === heuteKey) return "Heute";

  const bd = new Date(b + "T12:00:00Z");
  const hd = new Date(heuteKey + "T12:00:00Z");
  if (Number.isNaN(bd.getTime()) || Number.isNaN(hd.getTime())) return "Heute";

  const tage = Math.round((hd.getTime() - bd.getTime()) / 86400000);
  if (tage === 1) return "Gestern";
  // Innerhalb der letzten Woche sagt der Wochentag mehr als ein Datum.
  if (tage > 1 && tage < 7) {
    const namen = [
      "Sonntag", "Montag", "Dienstag", "Mittwoch",
      "Donnerstag", "Freitag", "Samstag",
    ];
    return namen[bd.getUTCDay()] ?? "Zuletzt";
  }
  // Älter — oder in der Zukunft (Uhr verstellt): ein Datum ist ehrlicher.
  if (tage <= 0) return "Heute";
  return `${bd.getUTCDate()}. ${
    ["Jan.", "Feb.", "März", "April", "Mai", "Juni", "Juli",
     "Aug.", "Sep.", "Okt.", "Nov.", "Dez."][bd.getUTCMonth()]
  }`;
}

/* 2026-08-26: `zuhauseZeichen()` stand hier. Dieselben Zeichen wurden an
   fuenf weiteren Stellen gebraucht (Kontakte, Haken, Schliessen, Dateiart) —
   also wohnt der Vorrat jetzt in zeichen.ts, EINMAL fuer die ganze App. */

/** Chip-Fragen und leere Fetzen gehören nicht unter Gesagt. */
export function istZuhauseGesagt(text: string): boolean {
  const t = reinigeGesagt(text);
  if (t.length < 8) return false;
  const norm = t.toLowerCase().replace(/[.?!]+$/g, "").trim();
  return !ZUHAUSE_WEGE.some(
    (w) => w.frage.toLowerCase().replace(/[.?!]+$/g, "").trim() === norm,
  );
}

export function reinigeGesagt(text: string): string {
  return text.replace(/\s*\[Audio:[^\]]*\]\s*/g, " ").replace(/\s+/g, " ").trim();
}

/** Ein Satz für die Startseite — kein Markdown, kein Rollup. */
export function kuerzeTagesbild(text: string): string {
  let s = text
    .replace(/^#{1,6}\s+.*$/gm, "")
    .replace(/\*\*?|__|`/g, "")
    .replace(/^\s*[-*]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
  const satz = s.match(/^(.+?[.!?])(\s|$)/);
  if (satz?.[1] && satz[1].length >= 12) {
    // Der Morgen-Satz bleibt ganz. Nur ein sehr langer Satz wird am Wort gekuerzt.
    return kuerze(satz[1], 220);
  }
  return kuerze(s, 160);
}

function kuerze(text: string, max: number): string {
  const t = reinigeGesagt(text);
  if (t.length <= max) return t;
  const schnitt = t.slice(0, max);
  const leer = schnitt.lastIndexOf(" ");
  const kern = leer >= 48 ? schnitt.slice(0, leer) : schnitt.trimEnd();
  return `${kern.trimEnd()}…`;
}

export type SpiegelStufe = "leer" | "anfang" | "tragfaehig" | "dicht";

export interface SpiegelTag {
  date: string;
  recordings: number;
  essence: boolean;
}

export interface SpiegelReife {
  anteil: number;
  prozent: number;
  stufe: SpiegelStufe;
  satz: string;
  heuteAufnahmen: number;
  tageMitStimme: number;
  farbe: "niedrig" | "mittel" | "gut" | "neutral";
}

/** Wie Whoop: der Ring ist HEUTE. Gestern zählt nicht in die Füllung. */
export function spiegelReife(
  tage: SpiegelTag[],
  heuteAufnahmen: number,
): SpiegelReife {
  const mitStimme = tage.filter((t) => t.recordings > 0).length;
  const heuteN = Math.max(0, heuteAufnahmen);
  const anteil = Math.min(1, heuteN / 2);
  const prozent = Math.round(anteil * 100);
  let stufe: SpiegelStufe;
  let satz: string;
  let farbe: SpiegelReife["farbe"];
  if (heuteN <= 0) {
    stufe = "leer";
    farbe = "niedrig";
    satz =
      mitStimme > 0
        ? "Neuer Tag. Noch keine Stimme heute — zwei Szenen, dann trägt der Spiegel wieder."
        : "Noch keine Stimme für den Spiegel. Zwei Szenen heute — dann kann ich dich zeigen.";
  } else if (heuteN === 1) {
    stufe = "anfang";
    farbe = "mittel";
    satz = "Eine Szene heute. Noch eine, dann trägt der Tages-Spiegel.";
  } else if (mitStimme >= 7) {
    stufe = "dicht";
    farbe = "gut";
    satz = "Dicht genug. Dein Spiegel hat Substanz.";
  } else {
    stufe = "tragfaehig";
    farbe = "gut";
    satz = "Genug für heute. Eine volle Woche Stimme macht den roten Faden.";
  }
  return {
    anteil,
    prozent,
    stufe,
    satz,
    heuteAufnahmen: heuteN,
    tageMitStimme: mitStimme,
    farbe,
  };
}
